import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';

const INACTIVITY_TIMEOUT_MS = 48 * 60 * 60 * 1000; // 48 hours
const ACTIVITY_UPDATE_THROTTLE_MS = 5000; // 5 seconds (to avoid excessive state updates)
const LAST_ACTIVE_KEY = 'auth_last_active';

export function useIdleTimeout() {
  const { logout, isAuthenticated } = useAuthStore();
  
  const [lastActiveAt, setLastActiveAt] = useState<number>(() => {
    const stored = localStorage.getItem(LAST_ACTIVE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  });
  const lastActiveRef = useRef<number>(lastActiveAt);
  const throttledUpdateRef = useRef<NodeJS.Timeout | null>(null);

  // Initial check on mount/auth to handle returning after a long time
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const timeSinceLastActive = Date.now() - lastActiveRef.current;
    if (timeSinceLastActive > INACTIVITY_TIMEOUT_MS) {
      console.log('User inactive for more than 48 hours across sessions. Signing out...');
      logout();
    }
  }, [isAuthenticated, logout]);

  // Helper function to update activity
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;
    
    // Throttle state updates to improve performance
    if (!throttledUpdateRef.current) {
      throttledUpdateRef.current = setTimeout(() => {
        const now = Date.now();
        lastActiveRef.current = now;
        setLastActiveAt(now);
        localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
        throttledUpdateRef.current = null;
      }, ACTIVITY_UPDATE_THROTTLE_MS);
    }
  }, [isAuthenticated]);

  // Set up event listeners for activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousemove', 'keydown', 'wheel', 'pointerdown', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (throttledUpdateRef.current) {
        clearTimeout(throttledUpdateRef.current);
      }
    };
  }, [handleActivity, isAuthenticated]);

  // Set up the interval that checks for timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check every minute if the user has been inactive for too long
    const checkInterval = setInterval(() => {
      const timeSinceLastActive = Date.now() - lastActiveRef.current;
      
      if (timeSinceLastActive > INACTIVITY_TIMEOUT_MS) {
        console.log('User inactive for more than 48 hours. Signing out...');
        clearInterval(checkInterval);
        logout();
      }
    }, 60000); // Check every 60 seconds

    return () => {
      clearInterval(checkInterval);
    };
  }, [isAuthenticated, logout]);

  // Clear local storage when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(LAST_ACTIVE_KEY);
    }
  }, [isAuthenticated]);

  return { lastActiveAt };
}
